import { Locales } from "@/lib/i18n";
import { getPermalink } from "@/lib/i18n/functions";
import { ProductDocument } from "@/prismicio-types";
import { ProductImage } from "@/types";
import { StoreProduct } from "@medusajs/types";
import { asText } from "@prismicio/client";

type ProductJSONLDProps = {
  product: ProductDocument;
  medusaProduct: StoreProduct | undefined;
  images: ProductImage[];
  locale: Locales;
  isOutOfStock: boolean;
};

const ProductJSONLD = ({ product, medusaProduct, images, locale, isOutOfStock }: ProductJSONLDProps) => {
  const baseUrl = process.env.NEXT_PUBLIC_FRONT_URL;
  const url = `${baseUrl}${getPermalink(locale, "product")}/${product.uid}`;

  const prices = (medusaProduct?.variants || [])
    .map((variant) => variant.calculated_price?.calculated_amount)
    .filter((price): price is number => typeof price === "number");

  const availability = isOutOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock";
  // Festi is a rental company, not a retailer: LeaseOut reflects the actual transaction offered.
  const businessFunction = "http://purl.org/goodrelations/v1#LeaseOut";

  const offers =
    prices.length > 1
      ? {
          "@type": "AggregateOffer",
          priceCurrency: "EUR",
          lowPrice: Math.min(...prices),
          highPrice: Math.max(...prices),
          offerCount: prices.length,
          availability,
          businessFunction,
        }
      : prices.length === 1
        ? {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: prices[0],
            url,
            availability,
            businessFunction,
          }
        : undefined;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.data.meta_title || product.data.title,
    description: product.data.meta_description || asText(product.data.description),
    image: images.map((image) => image.url),
    sku: medusaProduct?.variants?.[0]?.sku || "ACBROMO",
    brand: {
      "@type": "Brand",
      name: "Festi rent",
    },
    url,
    ...(offers && { offers }),
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
};

export default ProductJSONLD;
